import { ApplicationRef, Component, OnInit, ViewContainerRef } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MainRoute, RootRoute } from '../../../../../shared/models/angular-routing';
import { StormPanelComponent } from '../storm-panel/storm-panel.component';
import { MapPreviewComponent } from '../../../../../shared/components/map-preview/map-preview.component';
import { InsightsRoute } from '../../../insights.routes';
import { StormMapContent } from '../../../../../shared/components/map-preview/model/map-content/StormMapContent';
import { ConfigurationService } from '../../../../../configuration/configuration.service';
import { StormControlComponent } from '../storm-control/storm-control.component';
import { filter } from 'rxjs/operators';
import { StormInfo, StormShift, toStormInfo } from '../../../../../shared/models/storm.model';
import { StormService } from '../../../../../data/storms/storm.service';
import { StormMapDataService } from '../storm-map-data.service';
import { ConfigurationModel } from '../../../../../shared/models/configuration.model';
import { ObservationsService } from '../../../../../data/observations/observations.service';
import { Observation, ObservationType } from '../../../../../shared/models/observation';
import { Feature, FeatureCollection } from 'geojson';
import { ObservationManagementService } from '../../../../../data/observations/observation-management.service';
import { ObservationTypeGroup } from '../../../../../shared/models/observation-group';

@Component({
  selector: 'app-storm-detail',
  imports: [MatButton, MatIcon, MatIconButton, MatTooltip, StormPanelComponent, MapPreviewComponent, StormControlComponent, RouterOutlet],
  templateUrl: './storm-detail.component.html',
  styleUrl: './storm-detail.component.scss',
})
export class StormDetailComponent implements OnInit {
  isLoading = false;
  showingShiftDetail = false;
  stormId!: number;
  storm: StormInfo;
  stormShifts: StormShift[];
  stormObservations: Observation[];
  configuration: ConfigurationModel;

  mapContent: StormMapContent;
  filterUnixTime: number;
  speedFactor = 10;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly configurationService: ConfigurationService,
    private readonly stormService: StormService,
    private readonly stormMapDataService: StormMapDataService,
    private readonly observationService: ObservationsService,
    private readonly observationManagementService: ObservationManagementService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly appRef: ApplicationRef,
  ) {}

  ngOnInit() {
    this.stormId = this.activatedRoute.snapshot.params['stormId'];
    this.showingShiftDetail = this.router.url.includes('(shift:');

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => {
      this.showingShiftDetail = this.router.url.includes('(shift:');
    });

    this.configurationService.sharedConfigurationModel.subscribe(model => {
      if (model !== null) {
        this.configuration = model;
        // hit access to get signing cookie (only AWS env)
        this.stormService.getStormAccess(this.stormId).then(() => {
          Promise.all([
            this.stormService.getStorm(this.stormId),
            this.stormService.getStormShifts(this.stormId),
            model,
            this.stormService.getStormPlaybackIndex(model.id, this.stormId),
            this.observationService.getObservationsByStormId(this.stormId),
            this.observationService.getObservationTypes(),
            this.observationManagementService.getObservationTypeGroups(),
          ]).then(responses => {
            const [storm, stormShifts, configuration, playbackIndex, observations, observationTypes, observationTypeGroups] = responses;
            this.storm = toStormInfo(storm.data);
            this.stormShifts = stormShifts.data;

            // load observation type groups
            const observationTypeGroupsMap = new Map<number, ObservationTypeGroup>();
            const observationTypesMap = new Map<number, ObservationType>();
            for (const observationTypeGroup of observationTypeGroups) {
              observationTypeGroupsMap.set(observationTypeGroup.id, observationTypeGroup);
            }

            // load observation types
            for (const observationType of observationTypes) {
              observationTypesMap.set(
                observationType.id,
                new ObservationType(
                  observationType.id,
                  observationType.title,
                  !!observationType.abbreviation ? observationType.abbreviation : observationType.title,
                  observationType.observationTypeGroupId
                )
              );
            }
            // add deleted observation type
            observationTypesMap.set(-1, new ObservationType(-1, 'Deleted Observation Type', 'DELETED TYPE', -1));

            this.stormObservations = observations.data.map(observationModel => {
              const type = observationTypesMap.get(observationModel.typeId) ?? observationTypesMap.get(-1);
              type.group = observationTypeGroupsMap.get(type.observationTypeGroupId);
              return Observation.fromModel(observationModel, type, null, null);
            });
            const observationFeatureCollection = {
              type: 'FeatureCollection',
              features: this.stormObservations.map(o => this.observationToObservationFeature(o)),
            } as FeatureCollection;

            this.mapContent = new StormMapContent(
              configuration,
              this.configurationService.trackStyles,
              this.stormShifts,
              this.storm,
              playbackIndex,
              observationFeatureCollection,
              this.stormObservations,
              this.viewContainerRef,
              this.appRef,
              this.stormMapDataService,
              (unixtime: number) => {
                this.currentTimeChanged(unixtime);
              }
            );
          }).catch(error => {
            console.error(error);
          });
        }).catch(error => {
          console.error(error);
        });
      }
    });
  }

  goBack() {
    this.router
      .navigate(['../..', InsightsRoute.STORM_REPORT], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  closeInsight() {
    this.router
      .navigate([`/${RootRoute.MAIN}`, MainRoute.INSIGHTS], {
        queryParamsHandling: 'merge',
        queryParams: { search: undefined, address: undefined, visit: undefined },
      })
      .then();
  }

  filter(timeTo: number) {
    this.filterUnixTime = timeTo;
    this.mapContent?.filterTo(timeTo);
  }

  onSpeedUpdate(speed: number) {
    this.speedFactor = speed;
    this.mapContent.changeAnimationSpeed(this.speedFactor);
  }

  // Storm Map Content changed
  currentTimeChanged(unixTime: number) {
    this.filterUnixTime = unixTime;
  }

  private observationToObservationFeature(observation: Observation): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [observation.location.longitude, observation.location.latitude],
      },
      properties: {
        id: observation.id,
        title: observation.observationType.mapLabel,
        highlighted: false,
        color: !!observation.observationType.group?.mapColor ? observation.observationType.group.mapColor : undefined,
        time: Math.round(observation.created.valueOf() / 1000),
      },
    };
  }
}
